// enums
export enum PricingPlan {
  Trial = "Trial",
  Lite = "Lite",
  Pro = "Pro",
  Max = "Max",
  Flex = "Flex",
}

export enum BotStatusColor {
  "active" = "green",
  "pause" = "orange",
  "error" = "red",
  "stopped" = "default",
  "session expired" = "orange",
}

export enum RequestFeatureFilterCriteria {
  recent = "recent",
  popular = "popular",
}

export enum UserRoleColor {
  owner = "lime",
  admin = "purple",
  member = "geekblue",
}

export interface IRequestedFeature {
  title: string;
  description: string;
  status: string;
  votes: number;
}

export type RequestedFeatureStatusTitle =
  | "Under Consideration"
  | "Up Next"
  | "In Progress"
  | "Shipped";

export enum RequestedFeaturesStatusIndicatorColor {
  under_consideration = "orange",
  up_next = "green",
  in_progress = "blue",
  shipped = "red",
}

// interfaces
export interface IResponseError {
  status: number;
  text: string;
  reason?: string;
  details?: { [key: string]: string[] };
}

export interface ICheckAccessCodeParams {
  email: string;
  access_code: string;
}

export type UserStatus = "owner" | "admin" | "member";

export interface IUpworkProfile {
  id: number;
  username: string;
  is_selected: boolean;
  country: string | null;
  city: string | null;
  countryTimezone: string | null;
  userId: string;
  profileTitle: string;
  profileDescription: string;
  availability: string;
  skills: string[];
  hourly_rate?: number | null;
  portrait: {
    portrait: string | null;
    bigPortrait: string | null;
    portrait500: string | null;
    smallPortrait: string | null;
  } | null;
}

export interface IUser {
  id: string;
  email: string;
  team_id: string;
  invited_by: string | null;
  paid_user: boolean;
  can_use_pipedrive: boolean;
  role: UserStatus;
  full_name: string;
  picture: string;
  is_verified: boolean;
  show_plashka: boolean;
  bot_subscription_is_active: boolean;
  sync_start_date: string | null;
  trial_expires_at?: string;
  active_licenses_count: number;
  domain: string;
  plan: BillingPlans;
  teammates: number;
  teammates_paid: number;
  trial_date_start: string;
  trial_date_end: string;
  plan_start_date: string;
  plan_expire_date: string;
  ai_credits_used: number;
  ai_credits_total: number;
  ai_credits_extra?: number;
  byok?: {
    enabled: boolean;
    has_key: boolean;
    key_last4: string | null;
    status: "valid" | "invalid" | "no_credits" | null;
  };
  pipedrive_app: "public" | "private_prod" | "private_dev";
  promocode: IDiscount | null;
  billing_status?: string;
  has_active_subscription?: boolean;
  credits_reset_at?: string;
  upwork_profile?: IUpworkProfile | null;
  /**
   * Support-team flag. Used to keep the support inbox out of the navigation
   * and out of the router for everyone else. The inbox is protected by the
   * staff middleware on the API, not by this field.
   */
  is_staff: boolean;
}

export type ProfileFilterCriteriaType =
  | "current_week"
  | "last_week"
  | "current_month"
  | "last_month"
  | "3_months"
  | "6_months"
  | "current_year"
  | "all_time";

export interface IRegisterParams {
  email: string;
  picture?: string;
  full_name?: string;
  /** Cloudflare Turnstile token; required by the API once the widget is live. */
  turnstile_token?: string;
}

export interface IRefreshTokenParams {
  refresh_token: string;
}

export interface IRefreshTokenResponse {
  access_token: string;
  access_token_expires_at: string;
  refresh_token: string;
}

export interface IAuthResponse extends IRefreshTokenResponse {
  refresh_token: string;
  refresh_token_expires_at: string | null;
  user: IUser;
}

export interface ITeamMemberRecord {
  email: string;
  full_name: string | null;
  id: string;
  invited_by: string | null;
  is_verified: boolean;
  paid_user: boolean;
  picture: string | null;
  role: UserStatus;
  sync_start_date: string | null;
  team_id: string;
  active_licenses_count: number;
  domain: string;
  plan: BillingPlans;
  teammates: number;
  teammates_paid: number;
  trial_expires_at?: string;
}

export interface ITeamMembersResponse {
  data: ITeamMemberRecord[];
}

export interface IGetPipedriveConnectionResponse {
  data: {
    is_connected: boolean;
  };
}

export interface ICrmSettings {
  auto_create_company: boolean;
  deal_name?: string;
  create_deal: boolean;
  create_all_notes_in_deal: boolean;
  create_lead: boolean;
  create_notes_for_job: boolean;
  create_notes_for_bid: boolean;
  create_notes_for_all_messages: boolean;
  sync_start_date: string;
  ai_model: string;
}

export interface IJobSettings {
  login: string;
  password: string;
  secret_answer: string;
  search_url: string;
  name: string;
  title_profile: string;
  description_profile: string;
  availability: string;
  skills: string;
  chat_id: string;
  channel_id: string;
  evaluation_criteria: string;
  filter: string;
}

export interface IAISettings {
  automatic_job_ai_assistant: boolean;
}

export interface IAutoBidSettings {
  autobid_cover_letter_template_id: number | null;
  autobid_hourly_rate: number;
  autobid_rate_increase_frequency: string;
  autobid_rate_increase_percentage: number;
  autobid_fixed_price_adjustment: number;
  autobid_project_duration: string;
  autobid_match_prompt: string | null;
  autobid_red_flags_prompt: string | null;
  autobid_max_bids_per_day: number;
  autobid_max_connects_per_day: number;
  autobid_stop_on_low_connects: number;
  autobid_scan_interval: string;
  autobid_jobs_per_page: number;
  autobid_job_age_limit: string;
  autobid_min_match_score: number;
  autobid_skip_fixed_price: boolean;
  autobid_skip_hourly: boolean;
  autobid_active_hours_from: string | null;
  autobid_active_hours_to: string | null;
  autobid_timezone: string | null;
  // Profile Highlights (v4.1.0): how highlights are added to proposals.
  autobid_highlights_mode: "off" | "ai" | "manual";
  autobid_highlights_selection: IProfileHighlight[]; // manual mode, max 4, ordered
  // Smart Boosting відкладено (не завершено) - поля закоментовані:
  // autobid_smart_boost: boolean;
  // autobid_smart_boost_max_connects: number;
}

export interface IProfileHighlight {
  type: "job" | "portfolio" | "certificate";
  // Upwork contract/portfolio/certificate id as string (portfolio ids exceed 2^53)
  id: string;
  title: string;
}

// GET /api/upwork/profiles/{profileId}/highlights
export interface IHighlightsJob {
  id: string;
  title: string;
  rating: number | null;
  feedback: string | null;
  description: string | null;
  amount: number | null;
  currency: string | null;
  paymentType: "Fixed" | "Hourly" | null;
  startedOn: string | null;
  endedOn: string | null;
  skills?: string[];
}

export interface IHighlightsPortfolio {
  id: string;
  title: string;
  description: string | null;
  thumbnail: string | null;
  skills?: string[];
  url?: string | null;
  role?: string | null;
}

export interface IHighlightsCertificate {
  id: string;
  title: string;
  provider: string | null;
  issued: string | null;
  description: string | null;
}

export interface IProfileHighlightsResponse {
  jobs: IHighlightsJob[];
  portfolio: IHighlightsPortfolio[];
  certificates: IHighlightsCertificate[];
}

export interface IGetSettingsResponse {
  data: {
    crm: ICrmSettings;
    job_assistant: IAISettings;
    autobid: IAutoBidSettings;
  };
}

export interface IAutoBidJob {
  id: number;
  u_job_id: string;
  status: 'scanned' | 'matched' | 'rejected' | 'submitted' | 'failed' | 'skipped';
  match_score: number | null;
  reject_reason: string | null;
  data: {
    title?: string;
    budget?: string;
    jobType?: string;
    job?: {
      title?: string;
      description?: string;
      budget?: string;
      paymentType?: string;
      experienceLevel?: string;
      jobCategory?: string;
      requiredSkills?: string[];
      proposals?: number;
      jobPosted?: string;
      paymentVerification?: boolean;
      memberSince?: string;
      totalReviews?: number;
      hireRate?: string;
      totalSpent?: string;
      rating?: string;
      clientCountry?: string;
      clientCity?: string;
      clientJobsPosted?: number;
      clientJobsOpen?: number;
      clientCompanyName?: string;
      clientCompanyIndustry?: string;
      clientTimezone?: string;
      clientTotalCharges?: number;
      clientAvgHourlyRate?: number;
      clientActiveAssignments?: number;
      jobWorkload?: string;
      engagementDuration?: string;
      screeningQuestions?: string[];
      requiredConnects?: number;
      freelancerHourlyRate?: number;
      additionalSkills?: { name: string; relevance: string }[];
      applicantsBidsStats?: { avg?: number; min?: number; max?: number };
      qualificationsMatches?: { label: string; met: boolean; freelancerValue: string }[];
      extendedBudgetMin?: number;
      extendedBudgetMax?: number;
      isContractToHire?: boolean;
      isPremium?: boolean;
      projectType?: string;
      numberOfPositionsToHire?: number;
      invites?: number;
      interviews?: number;
      hired?: number;
      jobTotalApplicants?: number;
      jobTotalHired?: number;
      jobInvitationsSent?: number;
      jobLastBuyerActivity?: string;
      preferredRegions?: string[];
      requiredLanguages?: string[];
      minJobSuccessScore?: number;
      reviewsTitles?: string[];
      reviewsFeedback?: string[];
      enhancedWorkHistory?: { title: string; score: number; comment?: string; clientComment?: string; clientScore?: number; totalCharge?: number }[];
      openJobsTitles?: string[];
      isAiGenerated?: boolean;
      isFirstJobPost?: boolean;
    };
    submission?: {
      coverLetter?: string;
      answers?: { question: string; answer: string }[];
      rate?: number;
      templateName?: string;
    };
    match_result?: {
      match: boolean;
      score: number;
      verdict: string;
      summary: string;
      skill_match?: { matched: string[]; missing: string[]; overlap_percent: number };
      red_flags_check?: { passed: boolean; triggered: string[] };
      match_criteria?: { rule: string; passed: boolean; note: string }[];
      client_assessment?: { risk_level: string; notes: string };
    };
  } | null;
  created_at: string;
  updated_at: string;
}

export interface IAutoBidStats {
  total: number;
  scanned: number;
  matched: number;
  rejected: number;
  submitted: number;
  failed: number;
  skipped: number;
  replies: number;
}

export interface IAuthorizedPipedrive {
  access_code: string;
  state?: string;
}

export interface IAnalytics {
  ai_answers: number;
  ai_proposals: number;
  bids: number;
  opened_jobs: number;
  replies: number;
  reply_rate: number;
  sync_with_crms: number;
  direct_messages: number;
  invites: number;
  used_connects: number;
  boost_connects: number;
  offers_received: number;
  offers_accepted: number;
}

export interface IAnalyticsResponse {
  graph: {
    datasets: {
      opened_jobs: number[];
      bids: number[];
      replies: number[];
      ai_proposals: number[];
      ai_answers: number[];
      sync_with_crms: number[];
      reply_rate: number[];
      direct_messages: number[];
      invites: number[];
      used_connects: number[];
      boost_connects: number[];
      offers_received: number[];
      offers_accepted: number[];
    };
    labels: string[];
  };
  total: IAnalytics;
  previous_total?: IAnalytics;
}

export interface IPlan {
  created_at: string;
  currency: string;
  description: string[];
  id: number;
  is_archived: boolean;
  is_selected: boolean;
  period_name: string;
  is_trial: null | boolean;
  name: string;
  price: string;
  price_discounted?: string;
  yearly_price?: number;
  has_yearly?: boolean;
  ai_credits_per_seat?: number;
  updated_at: string;
}

export interface ICustomerPortalLink {
  customerPortalLink: string;
}

export type PaymentStatus =
  | "paid"
  | "not_paid"
  | "failed"
  | "trial"
  | "past_due"
  | "canceled";

export type OrderStatus = "processing" | "failed" | "approved";

export type BillingPlans =
  | "Trial"
  | "Lite"
  | "Pro"
  | "Max"
  | "Flex";


export interface IPaymentHistoryRecord {
  id: string;
  order_time: string;
  billing_plan_name: BillingPlans;
  billing_license_id: string;
  status: "paid" | "failed";
  stripe_details_url: string;
  masked_card: string;
  amount: string;
  currency: string;
}

export interface IJobScoreSettings {
  payment_verification: boolean;
  payment_verification_min: string;
  payment_verification_mid: string;
  payment_verification_max: string;
  rating: boolean;
  rating_min: string;
  rating_mid: string;
  rating_max: string;
  total_reviews: boolean;
  total_reviews_min: string;
  total_reviews_mid: string;
  total_reviews_max: string;
  hire_rate: boolean;
  hire_rate_min: string;
  hire_rate_mid: string;
  hire_rate_max: string;
}

export interface IProfileStats {
  upwex_user_id: string;
  email: string;
  bids: number;
  invites: number;
  direct_messages: number;
  replies: number;
  used_connects: number;
  boost_connects: number;
  reply_rate: number;
  offers_received: number;
  upwork_user: {
    id: number;
    username: string;
    user_id: string;
    portrait: {
      portrait: string;
      bigPortrait: string;
      portrait500: string;
      smallPortrait: string;
    };
    email: string;
  };
}

export interface IProfilesGeneralStatistics {
  profiles: IProfileStats[];
  total: {
    bids: number;
    invites: number;
    direct_messages: number;
    replies: number;
    used_connects: number;
    boost_connects: number;
    reply_rate: number;
    offers_received: number;
  };
}

export interface IProfilesAnalyticsResponse {
  data: IProfilesGeneralStatistics;
}

export interface IDiscount {
  code: string;
  discount_amount: number;
  expire_at: string;
}

export interface IPromoCodeAddResponse {
  data: IDiscount;
}

interface IProposalLink {
  url: string | null;
  label: string;
  active: boolean;
}

export interface IProposal {
  job_title: string;
  job_id: string;
  proposal_id: string;
  used_connects: number;
  is_reply: boolean;
}

export interface IGetProposalsResponse {
  data: IProposal[];
  user_data: {
    upwex_user_email: string;
    upwork_user_name: string;
  };
  pagination: {
    current_page: number;
    first_page_url: string;
    from: number;
    last_page: number;
    last_page_url: string;
    links: IProposalLink[];
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number;
    total: number;
  };
  requestId: number;
}

export type AnalyticsPeriod =
  | "current_week"
  | "last_week"
  | "current_month"
  | "last_month"
  | "last_3_months"
  | "last_year";

export type FieldsMappingTabs = "contact" | "lead" | "company";

export enum SUCCESS_MESSAGES {
  SIGN_UP = "Registration was successful. Please check your mail.",
}

export enum VALIDATION_MESSAGES {
  INVALID_EMAIL = "Oops! Invalid email address",
}

export enum ERROR_MESSAGES {
  GENERIC = "Something went wrong...Please try again later.",
  ACCESS_CODE_EXPIRED = "Access_code is expired. Please log in again.",
  AUTH_FAIL = "There is an active session already established for this device. Make sure to logout from Chrome extension to proceed.",
  FETCH_PIPEDRIVE_FAIL = "Failed to fetch pipedrive connection status.",
  SESSION_ALREADY_SEIZED = "Auth token is already in use.",
}

export enum ColumnAlign {
  CENTER = "center",
  LEFT = "left",
  RIGHT = "right",
  JUSTIFY = "justify",
  CHAR = "char",
}

export enum ModalType {
  Discounts = "discounts",
  InviteTeamMembers = "inviteTeamMembers",
  UpwexApiKey = "upwexApiKey",
  Offline = "offline",
  Feedback = "feedback",
}

// ----------------------------------------
// Support chat (replaces Intercom)
// Contract: hlb.upwex.io/.claude/docs/CHAT_API.md
// ----------------------------------------

export type ChatSenderType = "user" | "staff";

export type ChatConversationStatus = "open" | "closed";

export interface IChatAttachment {
  id: string;
  name: string;
  size: number;
  mime: string;
}

export interface IChatMessage {
  id: string;
  sender_type: ChatSenderType;
  sender_name: string | null;
  body: string;
  attachments: IChatAttachment[];
  read_at: string | null;
  created_at: string;
}

export interface IChatConversation {
  id: string;
  status: ChatConversationStatus;
  user_unread_count: number;
}

export interface IChatConversationResponse {
  conversation: IChatConversation;
  messages: IChatMessage[];
  has_more: boolean;
}

export interface IChatPollResponse {
  unread_count: number;
  last_message_at: string | null;
}

export interface ISendChatMessagePayload {
  body?: string;
  attachment_ids?: string[];
}

// ────────────────────────────────────────────────────────────────
// Support inbox (staff-only). Mirrors the staff half of the frozen
// contract in hlb.upwex.io/.claude/docs/CHAT_API.md. Kept separate from
// the widget's IChat* shapes so the two sides can move independently.
// ────────────────────────────────────────────────────────────────

export type SupportConversationStatus = "open" | "closed";

export type SupportSenderType = "user" | "staff";

export interface ISupportAttachment {
  id: string;
  name: string;
  size: number;
  mime: string;
}

export interface ISupportMessage {
  id: string;
  sender_type: SupportSenderType;
  /** null for imported messages */
  sender_name: string | null;
  body: string;
  attachments: ISupportAttachment[];
  /** null when unread */
  read_at: string | null;
  created_at: string;
}

export interface ISupportConversationUser {
  id: string;
  email: string;
  full_name: string | null;
  plan: string | null;
}

export interface ISupportConversation {
  id: string;
  status: SupportConversationStatus;
  last_message_at: string | null;
  staff_unread_count: number;
  /** empty string when the last message carried only an attachment */
  preview: string | null;
  user: ISupportConversationUser;
}

export interface ISupportConversationsResponse {
  data: ISupportConversation[];
  meta: {
    current_page: number;
    last_page: number;
    total: number;
  };
}

export interface ISupportThread {
  id: string;
  status: SupportConversationStatus;
  user_unread_count: number;
}

export interface ISupportThreadResponse {
  conversation: ISupportThread;
  /** oldest first */
  messages: ISupportMessage[];
  has_more: boolean;
}

export interface ISupportContextUser {
  email: string;
  full_name: string | null;
  role: string | null;
  created_at: string | null;
  signup_attribution: Record<string, string> | null;
}

/** Every field is null or 0 when the user has no team. */
export interface ISupportContextBilling {
  plan: string | null;
  billing_status: string | null;
  trial_date_start: string | null;
  trial_date_end: string | null;
  plan_start_date: string | null;
  plan_expire_date: string | null;
  ai_credits_total: number;
  ai_credits_used: number;
  seats_total: number;
  seats_used: number;
  promocode: string | null;
  stripe_url: string | null;
}

export interface ISupportContextActivity {
  job_analyses: number;
  ai_proposals: number;
  ai_answers: number;
  auto_bid_jobs: number;
  last_seen_at: string | null;
}

export interface ISupportContextResponse {
  user: ISupportContextUser;
  billing: ISupportContextBilling;
  activity_30d: ISupportContextActivity;
}

export interface ISupportConversationsParams {
  status?: SupportConversationStatus;
  search?: string;
  page?: number;
}

/**
 * Counters behind the sidebar badge. `conversations` is how many open threads
 * carry an unread user message, `messages` is the sum of those counters.
 * Staff only - the endpoint answers 404 to everyone else.
 */
export interface ISupportUnreadSummary {
  conversations: number;
  messages: number;
}
